import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

interface Note {
  id: string;
  customerId: string;
  text: string;
  authorEmail: string;
  authorName: string | null;
  createdAt: string;
  attachments: Attachment[];
}

interface Attachment {
  id: string;
  filename: string;
  sizeBytes: number | null;
  mimeType: string | null;
}

export function NotesPanel({
  customerId,
  customerName,
}: {
  customerId: string;
  customerName: string;
}) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ notes: Note[] }>(
        `/api/notes?customer_id=${encodeURIComponent(customerId)}`,
      );
      setNotes(data.notes);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    setError(null);
    try {
      await apiFetch('/api/notes', {
        method: 'POST',
        body: JSON.stringify({
          customer_id: customerId,
          customer_name: customerName,
          text,
        }),
      });
      setDraft('');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this note?')) return;
    const prev = notes;
    setNotes(notes.filter((n) => n.id !== id));
    try {
      await apiFetch(`/api/notes/${id}`, { method: 'DELETE' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setNotes(prev);
    }
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <div className="composer">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={`Add a note about ${customerName}…`}
          rows={3}
        />
        <div className="composer-foot">
          <span className="char-count">{draft.length} characters</span>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={posting || !draft.trim()}
          >
            {posting ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {loading && notes.length === 0 ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <div className="loading-text">Loading notes…</div>
        </div>
      ) : notes.length === 0 ? (
        <div className="empty-state">
          <div className="glyph">✎</div>
          <div className="empty-head">No notes yet</div>
          <div className="empty-sub">
            Capture conversations, meeting notes, internal context — anything
            teammates need to know about{' '}
            <strong style={{ color: 'var(--text-primary)' }}>
              {customerName}
            </strong>
            .
          </div>
        </div>
      ) : (
        <div>
          {notes.map((n) => (
            <div className="note-card" key={n.id}>
              <div className="note-head">
                <div>
                  <span className="note-author">
                    {n.authorName || n.authorEmail}
                  </span>
                  <span className="note-time">{fmtRelative(n.createdAt)}</span>
                </div>
                <button className="note-delete" onClick={() => remove(n.id)}>
                  Delete
                </button>
              </div>
              <div className="note-text">{n.text}</div>
              {n.attachments.length > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: '1px solid var(--border-light)',
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {n.attachments.map((a) => (
                    <span key={a.id} style={{ marginRight: 12 }}>
                      📎 {a.filename}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diffMin = Math.round((Date.now() - t) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
  const diffDays = Math.round(diffMin / (60 * 24));
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Date(iso).toLocaleDateString();
}
